import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { addSchemaKeyHook, SchemaBase, getDefaultSchemaOptions, SchemaConstructorArgs } from "../schemaBase.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";

const SCHEMA_KEY = "scdTestEntities" as const;

/**
 * Minimal SCD schema used in tests for the generic SCD operations layer.
 *
 * Has one domain field (`label`) on top of the SCD bookkeeping fields so tests can
 * verify field carry-forward, versionIsCurrent invariants, and version transitions without
 * depending on any production entity.
 *
 * The partial unique index on `label` mirrors the recommended pattern for production
 * entities: `{ versionIsCurrent: true, deleted: false }` as the partial filter.
 */
@index({ label: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "scdTestEntities" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<ScdTestEntity>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ScdTestEntity extends ScdDocument<ScdTestEntity> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    public _id!: mongoose.Types.ObjectId;

    /**
     * The single domain field. Used in tests to verify field carry-forward,
     * conflict detection, and version transitions.
     */
    @prop({ type: () => String, required: true })
    public label!: string;

    constructor(properties?: SchemaConstructorArgs<ScdTestEntity>) {
        super(properties);
        Object.assign(this, properties);
    }
}
